import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 15000,
});

// ─── Auth Interceptor ─────────────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('starfuel_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response Interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('401 on:', error.config?.url);
      // Token expired — clear auth and redirect
      localStorage.removeItem('starfuel_token');
      localStorage.removeItem('starfuel_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// ─── Restaurants ──────────────────────────────────────────────────────────────
export const restaurantAPI = {
  getMe: () => api.get('/restaurants/me'),
  updateMe: (data) => api.put('/restaurants/me', data),
  getAll: (params) => api.get('/restaurants', { params }),
  getStats: () => api.get('/restaurants/me/stats'),
  getPreferredDrivers: () => api.get('/restaurants/me/preferred-drivers'),
  addPreferredDriver: (code) => api.post('/restaurants/me/preferred-drivers', { code }),
  removePreferredDriver: (driverId) => api.delete(`/restaurants/me/preferred-drivers/${driverId}`),
};

// ─── Drivers ─────────────────────────────────────────────────────────────────
export const driverAPI = {
  getMe: () => api.get('/drivers/me'),
  updateLocation: (lat, lng) => api.put('/drivers/me/location', { latitude: lat, longitude: lng }),
  updateStatus: (status) => api.put('/drivers/me/status', { status }),
  getMyJobs: (status) => api.get('/drivers/me/jobs', { params: { status } }),
  getAll: (params) => api.get('/drivers', { params }),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  getMyCode: () => api.get('/drivers/me/code'),
  getPreferredRequests: () => api.get('/drivers/me/preferred-requests'),
  respondToPreferredRequest: (restaurantId, action) => api.put(`/drivers/me/preferred-requests/${restaurantId}`, { action }),
};

// ─── Deliveries ───────────────────────────────────────────────────────────────
export const deliveryAPI = {
  create: (data) => api.post('/deliveries', data),
  getAll: (params) => api.get('/deliveries', { params }),
  getById: (id) => api.get(`/deliveries/${id}`),
  updateStatus: (id, status, reason) => api.put(`/deliveries/${id}/status`, { status, reason }),
  assign: (id, driverId) => api.post(`/deliveries/${id}/assign`, { driverId }),
};

// ─── Payments ─────────────────────────────────────────────────────────────────
export const paymentAPI = {
  getPlans: () => api.get('/payments/plans'),
  createCheckout: (plan) => api.post('/payments/checkout', { plan }),
  getHistory: () => api.get('/payments/history'),
  getPricingEstimate: (volume) => api.get('/payments/pricing-estimate', { params: { volume } }),
  openBillingPortal: () => api.post('/billing/portal'),
  chargeDelivery: (deliveryId) => api.post(`/billing/charge-delivery/${deliveryId}`),
};

// ─── Finance / payouts ────────────────────────────────────────────────────────
export const financeAPI = {
  getDriverEarnings: (id) => api.get(`/finance/drivers/${id}/earnings`),
  getDriverPayoutStatus: (id) => api.get(`/finance/drivers/${id}/payout-status`),
  onboardDriver: (id) => api.post(`/finance/drivers/${id}/onboard`),
  payoutDelivery: (id) => api.post(`/finance/deliveries/${id}/payout`),
};

export default api;
